import React, { useMemo, useState } from "react";
import { useGetGiftedUserSharesQuery } from "../api";
import { UserAutocomplete } from "../components/UserAutocomplete";
import { TransferSharesForm } from "../components/TransferSharesForm";
import Notification from "../components/ui/Notification";
import { useNotification } from "../hooks/useNotification";
import type { UserSharesSummary } from "../api/admin";
import {UserSharesModal} from "../components/modals/UserSharesModal";
import { ListIcon } from "lucide-react";
type SortField = "quantity" | "first_time" | "last_time" | null;
type SortDirection = "asc" | "desc";

export const GiftedSharesPage: React.FC = () => {
    const { notification, hideNotification } = useNotification();
    const [searchUserId, setSearchUserId] = useState<string>("");
    const [sortField, setSortField] = useState<SortField>("quantity");
    const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
    const [showUserGiftedSharesModal, setShowUserGiftedSharesModal] = useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [selectedUserData, setSelectedUserData] = useState<UserSharesSummary["user"] | null>(null);
    const {
        data: response,
        isLoading,
        refetch,
    } = useGetGiftedUserSharesQuery();

    const allItems: UserSharesSummary[] = response?.data || [];

    const filteredAndSorted = useMemo(() => {
        let items = [...allItems];

        if (searchUserId) {
            const userId = parseInt(searchUserId, 10);
            if (!isNaN(userId)) {
                items = items.filter((i) => i.user_id === userId);
            }
        }

        if (sortField) {
            items.sort((a, b) => {
                let aValue: number | string | null = null;
                let bValue: number | string | null = null;

                switch (sortField) {
                    case "quantity":
                        aValue = a.quantity;
                        bValue = b.quantity;
                        break;
                    case "first_time":
                        aValue = a.first_time || "";
                        bValue = b.first_time || "";
                        break;
                    case "last_time":
                        aValue = a.last_time || "";
                        bValue = b.last_time || "";
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) {
                    return sortDirection === "asc" ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortDirection === "asc" ? 1 : -1;
                }
                return 0;
            });
        }

        return items;
    }, [allItems, searchUserId, sortField, sortDirection]);

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) {
            return (
                <span className="text-gray-400 text-xs" aria-hidden="true">
                    ⇅
                </span>
            );
        }
        return (
            <span
                className="text-blue-600 text-xs"
                aria-hidden="true"
            >
                {sortDirection === "asc" ? "↑" : "↓"}
            </span>
        );
    };

    const formatDateTime = (value: string | null): string => {
        if (!value) return "—";
        try {
            return new Date(value).toLocaleString("ru-RU");
        } catch {
            return value;
        }
    };

    const handleShowUserGiftedSharesModal = (userId: number, userData: UserSharesSummary["user"]) => {
        setShowUserGiftedSharesModal(true);
        setSelectedUserId(userId);
        setSelectedUserData(userData);
    };

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Подарочные ваучеры
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Пользователи, у которых есть подарочные акции
                    </p>
                </div>
                <button
                    onClick={() => refetch()}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    Обновить
                </button>
            </div>

            {/* Форма переноса акций */}
            <TransferSharesForm sharesType={0} onSuccess={() => refetch()} />

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 mb-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Поиск по пользователю
                        </label>
                        <UserAutocomplete
                            value={searchUserId}
                            onChange={setSearchUserId}
                            placeholder="Введите ID, email, имя..."
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setSearchUserId("");
                                setSortField("quantity");
                                setSortDirection("desc");
                            }}
                            className="w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            Сбросить фильтры
                        </button>
                    </div>
                </div>
            </div>

            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
                </div>
            ) : filteredAndSorted.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                        {allItems.length === 0
                            ? "Нет пользователей с подарочными акциями"
                            : "Не найдено записей по заданным фильтрам"}
                    </p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50 dark:bg-gray-700">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Пользователь
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("quantity")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Количество акций
                                            {getSortIcon("quantity")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Общая стоимость
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("first_time")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Первая выдача
                                            {getSortIcon("first_time")}
                                        </button>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        <button
                                            onClick={() => handleSort("last_time")}
                                            className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                                        >
                                            Последняя выдача
                                            {getSortIcon("last_time")}
                                        </button>
                                            </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                        Действия
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {filteredAndSorted.map((item) => (
                                    <tr
                                        key={item.user_id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {item.user
                                                    ? `${item.user.name} ${item.user.surname}`
                                                    : `ID: ${item.user_id}`}
                                            </div>
                                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                                {item.user?.email || "—"}
                                            </div>
                                            <div className="text-xs text-gray-400 dark:text-gray-500">
                                                ID: {item.user_id}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {item.quantity}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {item.total_value.toFixed(2)} $
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatDateTime(item.first_time)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {formatDateTime(item.last_time)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            <button
                                                onClick={() => {
                                                    handleShowUserGiftedSharesModal(item.user_id, item.user);
                                                }}
                                                title="Список подарочных акций для пользователя"
                                            >
                                                <ListIcon size={24} color="green" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showUserGiftedSharesModal && (
                <UserSharesModal
                    sharesType={0}
                    userId={selectedUserId}
                    userData={selectedUserData}
                    onSharesUpdate={refetch}
                    onClose={() => {
                        setShowUserGiftedSharesModal(false);
                        setSelectedUserId(null);
                    }}
                />
            )}

            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />
        </div>
    );
};


